import type { BillingCycle, BillingPlan } from '@/lib/api';
import {
  LONG_CYCLES,
  cycleDiscountPercent,
  cycleMonthlyEquivalentCents,
} from '@/lib/billing-cycle';

/**
 * Modelo de exibicao puro do showcase de precos por ciclo (/pricing publico).
 *
 * Toda a aritmetica fica aqui, isolada do React, para ser testavel sem
 * montar componente: dado um plano + ciclo selecionado, devolve o que o
 * cartao mostra (preco mensal exibido, preco riscado, % de desconto, valor
 * total do ciclo). Os PRECOS e o DESCONTO sao dinamicos - vem de
 * Plan.priceCents (mensal) + PlanPrice (ciclos longos, definidos pelo Super
 * Admin); o desconto e emergente (mensal x meses vs preco do ciclo). Nenhum
 * percentual fixo de negocio mora no codigo.
 */
export interface PlanCycleView {
  isPaid: boolean;
  /** Preco principal exibido (centavos): mensal, ou equivalente mensal do ciclo. */
  displayMonthlyCents: number | null;
  /** Preco mensal cheio para riscar (centavos) - so quando ha desconto. */
  struckMonthlyCents: number | null;
  /** Desconto emergente do ciclo (% inteiro >=1) ou null. */
  discountPercent: number | null;
  /** Valor total cobrado no ciclo (centavos) - so em ciclos longos com preco. */
  totalCycleCents: number | null;
  /** true quando o plano e pago mas nao tem preco para o ciclo longo selecionado. */
  cycleUnavailable: boolean;
}

function activeCyclePriceCents(
  plan: BillingPlan,
  cycle: BillingCycle,
): number | null {
  const row = plan.prices?.find(
    (p) => p.cycle === cycle && (p.active ?? true) && p.priceCents > 0,
  );
  return row ? row.priceCents : null;
}

/** Constroi o modelo de exibicao de um plano para o ciclo selecionado. */
export function buildPlanCycleView(
  plan: BillingPlan,
  cycle: BillingCycle,
): PlanCycleView {
  const base: PlanCycleView = {
    isPaid: plan.priceCents > 0,
    displayMonthlyCents: null,
    struckMonthlyCents: null,
    discountPercent: null,
    totalCycleCents: null,
    cycleUnavailable: false,
  };

  // Plano gratuito: sempre "Grátis", ciclo nao se aplica.
  if (!base.isPaid) return base;

  // Mensal: preco do proprio plano, sem desconto.
  if (cycle === 'MONTHLY') {
    return { ...base, displayMonthlyCents: plan.priceCents };
  }

  // Ciclo longo: depende de PlanPrice cadastrado pelo Super Admin.
  const cycleCents = activeCyclePriceCents(plan, cycle);
  if (cycleCents === null) {
    return { ...base, cycleUnavailable: true };
  }

  const pct = cycleDiscountPercent(plan.priceCents, cycleCents, cycle);
  return {
    ...base,
    displayMonthlyCents: cycleMonthlyEquivalentCents(cycleCents, cycle),
    struckMonthlyCents: pct ? plan.priceCents : null,
    discountPercent: pct,
    totalCycleCents: cycleCents,
  };
}

export interface CycleOption {
  cycle: BillingCycle;
  /** Maior desconto disponivel nesse ciclo entre os planos pagos, ou null. */
  maxDiscountPercent: number | null;
  /** true quando o desconto varia entre planos (mostrar "até"). */
  discountVaries: boolean;
}

/**
 * Opcoes do toggle de ciclo. Mensal sempre presente. Um ciclo longo so vira
 * opcao se ao menos um plano pago tiver preco cadastrado para ele - assim a
 * vitrine nunca mostra um ciclo vazio. O desconto exibido na opcao e o maior
 * desconto disponivel naquele ciclo; se varia entre planos, sinaliza "até".
 */
export function buildCycleOptions(plans: BillingPlan[]): CycleOption[] {
  const options: CycleOption[] = [
    { cycle: 'MONTHLY', maxDiscountPercent: null, discountVaries: false },
  ];

  for (const cycle of LONG_CYCLES) {
    const discounts: number[] = [];
    for (const plan of plans) {
      if (plan.priceCents <= 0) continue;
      const cycleCents = activeCyclePriceCents(plan, cycle);
      if (cycleCents === null) continue;
      const pct = cycleDiscountPercent(plan.priceCents, cycleCents, cycle);
      if (pct) discounts.push(pct);
    }
    if (discounts.length === 0) continue;
    const max = Math.max(...discounts);
    const varies = discounts.some((d) => d !== discounts[0]);
    options.push({ cycle, maxDiscountPercent: max, discountVaries: varies });
  }

  return options;
}
